import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient, { auth } from '../../services/api/client';
import {
  Shield, ExternalLink, Download, Filter, Eye, EyeOff, Maximize2, Info, Zap,
  Brain, Sparkles, Bot, Lightbulb, RefreshCw, Copy, ChevronDown, ChevronUp,
  Target, AlertTriangle, Network, Server, Lock, Database, Key, Terminal,
  FileText, Search, Layers, Activity, ArrowLeft, HelpCircle, BookOpen,
  Factory, Cpu, Radio, Wifi, Settings, CheckCircle2
} from 'lucide-react';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';
import { toast } from '../../utils/toastUtils';

// MITRE ATT\u202F&\u202FCK Tactics Reference
const MITRE_TACTICS_REF = [
  { id: 'TA0001', name: 'Initial Access', description: 'Techniques pour obtenir un premier accès au réseau.', examples: 'Phishing, Exploitation services', color: '#ef4444' },
  { id: 'TA0002', name: 'Execution', description: 'Techniques pour exécuter du code malveillant.', examples: 'Scripts PowerShell, Macros', color: '#f97316' },
  { id: 'TA0003', name: 'Persistence', description: 'Techniques pour maintenir un accès persistant.', examples: 'Tâches planifiées, Services', color: '#eab308' },
  { id: 'TA0004', name: 'Privilege Escalation', description: 'Techniques pour obtenir des privilèges élevés.', examples: 'Exploit kernel, Token manipulation', color: '#22c55e' },
  { id: 'TA0005', name: 'Defense Evasion', description: 'Techniques pour éviter la détection.', examples: 'Obfuscation, Rootkits', color: '#14b8a6' },
  { id: 'TA0006', name: 'Credential Access', description: 'Techniques pour voler des identifiants.', examples: 'Keylogging, LSASS dump', color: '#3b82f6' },
  { id: 'TA0007', name: 'Discovery', description: 'Techniques de reconnaissance interne.', examples: 'Énumération AD, Scan réseau', color: '#6366f1' },
  { id: 'TA0008', name: 'Lateral Movement', description: 'Techniques pour se déplacer latéralement.', examples: 'RDP, PsExec, WMI', color: '#8b5cf6' },
  { id: 'TA0009', name: 'Collection', description: 'Techniques pour collecter des données.', examples: 'Screenshots, Keylogging', color: '#a855f7' },
  { id: 'TA0010', name: 'Exfiltration', description: 'Techniques pour exfiltrer des données.', examples: 'C2, DNS tunneling', color: '#d946ef' },
  { id: 'TA0011', name: 'Command & Control', description: 'Techniques de communication C2.', examples: 'HTTP/HTTPS, Domain fronting', color: '#ec4899' },
  { id: 'TA0040', name: 'Impact', description: 'Techniques pour perturber les systèmes.', examples: 'Ransomware, Wiper', color: '#f43f5e' }
];

// MITRE ATT\u202F&\u202FCK v18 - Enterprise Matrix
const MITRE_MATRIX = {
  tactics: [
    { id: 'TA0043', name: 'Reconnaissance', shortName: 'Recon', color: '#a855f7', description: 'Collecte d\'informations pour planifier les opérations' },
    { id: 'TA0042', name: 'Resource Development', shortName: 'Resource Dev', color: '#8b5cf6', description: 'Établissement de ressources pour les opérations' },
    { id: 'TA0001', name: 'Initial Access', shortName: 'Initial Access', color: '#ef4444', description: 'Obtention d\'un accès initial au réseau' },
    { id: 'TA0002', name: 'Execution', shortName: 'Execution', color: '#f97316', description: 'Exécution de code malveillant' },
    { id: 'TA0003', name: 'Persistence', shortName: 'Persistence', color: '#eab308', description: 'Maintien de l\'accès au système' },
    { id: 'TA0004', name: 'Privilege Escalation', shortName: 'Priv Esc', color: '#22c55e', description: 'Obtention de privilèges élevés' },
    { id: 'TA0005', name: 'Defense Evasion', shortName: 'Def Evasion', color: '#14b8a6', description: 'Évitement des mécanismes de détection' },
    { id: 'TA0006', name: 'Credential Access', shortName: 'Cred Access', color: '#3b82f6', description: 'Vol d\'identifiants et de credentials' },
    { id: 'TA0007', name: 'Discovery', shortName: 'Discovery', color: '#6366f1', description: 'Découverte de l\'environnement cible' },
    { id: 'TA0008', name: 'Lateral Movement', shortName: 'Lateral Mov', color: '#8b5cf6', description: 'Déplacement latéral dans le réseau' },
    { id: 'TA0009', name: 'Collection', shortName: 'Collection', color: '#a855f7', description: 'Collecte de données d\'intérêt' },
    { id: 'TA0011', name: 'Command and Control', shortName: 'C2', color: '#ec4899', description: 'Communication avec les systèmes compromis' },
    { id: 'TA0010', name: 'Exfiltration', shortName: 'Exfiltration', color: '#d946ef', description: 'Vol et extraction de données' },
    { id: 'TA0040', name: 'Impact', shortName: 'Impact', color: '#f43f5e', description: 'Manipulation, interruption ou destruction' }
  ],
  techniques: {
    'TA0043': [
      { id: 'T1595', name: 'Active Scanning', subtechniques: ['T1595.001', 'T1595.002', 'T1595.003'] },
      { id: 'T1592', name: 'Gather Victim Host Information', subtechniques: ['T1592.001', 'T1592.002', 'T1592.003', 'T1592.004'] },
      { id: 'T1589', name: 'Gather Victim Identity Information', subtechniques: ['T1589.001', 'T1589.002', 'T1589.003'] },
      { id: 'T1590', name: 'Gather Victim Network Information', subtechniques: ['T1590.001', 'T1590.002', 'T1590.003', 'T1590.004', 'T1590.005', 'T1590.006'] },
      { id: 'T1591', name: 'Gather Victim Org Information', subtechniques: ['T1591.001', 'T1591.002', 'T1591.003', 'T1591.004'] },
      { id: 'T1598', name: 'Phishing for Information', subtechniques: ['T1598.001', 'T1598.002', 'T1598.003'] },
      { id: 'T1597', name: 'Search Closed Sources', subtechniques: ['T1597.001', 'T1597.002'] },
      { id: 'T1596', name: 'Search Open Technical Databases', subtechniques: ['T1596.001', 'T1596.002', 'T1596.003', 'T1596.004', 'T1596.005'] },
      { id: 'T1593', name: 'Search Open Websites/Domains', subtechniques: ['T1593.001', 'T1593.002', 'T1593.003'] },
      { id: 'T1594', name: 'Search Victim-Owned Websites' }
    ],
    'TA0042': [
      { id: 'T1583', name: 'Acquire Infrastructure', subtechniques: ['T1583.001', 'T1583.002', 'T1583.003', 'T1583.004', 'T1583.005', 'T1583.006', 'T1583.007', 'T1583.008'] },
      { id: 'T1586', name: 'Compromise Accounts', subtechniques: ['T1586.001', 'T1586.002', 'T1586.003'] },
      { id: 'T1584', name: 'Compromise Infrastructure', subtechniques: ['T1584.001', 'T1584.002', 'T1584.003', 'T1584.004', 'T1584.005', 'T1584.006', 'T1584.007'] },
      { id: 'T1587', name: 'Develop Capabilities', subtechniques: ['T1587.001', 'T1587.002', 'T1587.003', 'T1587.004'] },
      { id: 'T1585', name: 'Establish Accounts', subtechniques: ['T1585.001', 'T1585.002', 'T1585.003'] },
      { id: 'T1588', name: 'Obtain Capabilities', subtechniques: ['T1588.001', 'T1588.002', 'T1588.003', 'T1588.004', 'T1588.005', 'T1588.006'] },
      { id: 'T1608', name: 'Stage Capabilities', subtechniques: ['T1608.001', 'T1608.002', 'T1608.003', 'T1608.004', 'T1608.005', 'T1608.006'] }
    ],
    'TA0001': [
      { id: 'T1189', name: 'Drive-by Compromise' },
      { id: 'T1190', name: 'Exploit Public-Facing Application' },
      { id: 'T1133', name: 'External Remote Services' },
      { id: 'T1200', name: 'Hardware Additions' },
      { id: 'T1566', name: 'Phishing', subtechniques: ['T1566.001', 'T1566.002', 'T1566.003', 'T1566.004'] },
      { id: 'T1091', name: 'Replication Through Removable Media' },
      { id: 'T1195', name: 'Supply Chain Compromise', subtechniques: ['T1195.001', 'T1195.002', 'T1195.003'] },
      { id: 'T1199', name: 'Trusted Relationship' },
      { id: 'T1078', name: 'Valid Accounts', subtechniques: ['T1078.001', 'T1078.002', 'T1078.003', 'T1078.004'] }
    ],
    'TA0002': [
      { id: 'T1059', name: 'Command and Scripting Interpreter', subtechniques: ['T1059.001', 'T1059.002', 'T1059.003', 'T1059.004', 'T1059.005', 'T1059.006', 'T1059.007', 'T1059.008', 'T1059.009'] },
      { id: 'T1609', name: 'Container Administration Command' },
      { id: 'T1610', name: 'Deploy Container' },
      { id: 'T1203', name: 'Exploitation for Client Execution' },
      { id: 'T1559', name: 'Inter-Process Communication', subtechniques: ['T1559.001', 'T1559.002', 'T1559.003'] },
      { id: 'T1106', name: 'Native API' },
      { id: 'T1053', name: 'Scheduled Task/Job', subtechniques: ['T1053.001', 'T1053.002', 'T1053.003', 'T1053.005', 'T1053.006', 'T1053.007'] },
      { id: 'T1129', name: 'Shared Modules' },
      { id: 'T1072', name: 'Software Deployment Tools' },
      { id: 'T1569', name: 'System Services', subtechniques: ['T1569.001', 'T1569.002'] },
      { id: 'T1204', name: 'User Execution', subtechniques: ['T1204.001', 'T1204.002', 'T1204.003'] },
      { id: 'T1047', name: 'Windows Management Instrumentation' }
    ],
    'TA0003': [
      { id: 'T1098', name: 'Account Manipulation', subtechniques: ['T1098.001', 'T1098.002', 'T1098.003', 'T1098.004', 'T1098.005', 'T1098.006'] },
      { id: 'T1197', name: 'BITS Jobs' },
      { id: 'T1547', name: 'Boot or Logon Autostart Execution', subtechniques: ['T1547.001', 'T1547.002', 'T1547.003', 'T1547.004', 'T1547.005'] },
      { id: 'T1037', name: 'Boot or Logon Initialization Scripts', subtechniques: ['T1037.001', 'T1037.002', 'T1037.003', 'T1037.004', 'T1037.005'] },
      { id: 'T1176', name: 'Browser Extensions' },
      { id: 'T1554', name: 'Compromise Client Software Binary' },
      { id: 'T1136', name: 'Create Account', subtechniques: ['T1136.001', 'T1136.002', 'T1136.003'] },
      { id: 'T1543', name: 'Create or Modify System Process', subtechniques: ['T1543.001', 'T1543.002', 'T1543.003', 'T1543.004'] },
      { id: 'T1546', name: 'Event Triggered Execution', subtechniques: ['T1546.001', 'T1546.002', 'T1546.003', 'T1546.004', 'T1546.005'] }
    ],
    'TA0004': [
      { id: 'T1548', name: 'Abuse Elevation Control Mechanism', subtechniques: ['T1548.001', 'T1548.002', 'T1548.003', 'T1548.004', 'T1548.005'] },
      { id: 'T1134', name: 'Access Token Manipulation', subtechniques: ['T1134.001', 'T1134.002', 'T1134.003', 'T1134.004', 'T1134.005'] },
      { id: 'T1068', name: 'Exploitation for Privilege Escalation' },
      { id: 'T1055', name: 'Process Injection', subtechniques: ['T1055.001', 'T1055.002', 'T1055.003', 'T1055.004', 'T1055.005'] }
    ],
    'TA0005': [
      { id: 'T1140', name: 'Deobfuscate/Decode Files or Information' },
      { id: 'T1006', name: 'Direct Volume Access' },
      { id: 'T1562', name: 'Impair Defenses', subtechniques: ['T1562.001', 'T1562.002', 'T1562.003', 'T1562.004'] },
      { id: 'T1070', name: 'Indicator Removal', subtechniques: ['T1070.001', 'T1070.002', 'T1070.003', 'T1070.004', 'T1070.005'] },
      { id: 'T1036', name: 'Masquerading', subtechniques: ['T1036.001', 'T1036.002', 'T1036.003', 'T1036.004', 'T1036.005'] },
      { id: 'T1027', name: 'Obfuscated Files or Information', subtechniques: ['T1027.001', 'T1027.002', 'T1027.003', 'T1027.004', 'T1027.005'] },
      { id: 'T1014', name: 'Rootkit' }
    ],
    'TA0006': [
      { id: 'T1557', name: 'Adversary-in-the-Middle', subtechniques: ['T1557.001', 'T1557.002', 'T1557.003'] },
      { id: 'T1110', name: 'Brute Force', subtechniques: ['T1110.001', 'T1110.002', 'T1110.003', 'T1110.004'] },
      { id: 'T1555', name: 'Credentials from Password Stores', subtechniques: ['T1555.001', 'T1555.002', 'T1555.003', 'T1555.004', 'T1555.005', 'T1555.006'] },
      { id: 'T1212', name: 'Exploitation for Credential Access' },
      { id: 'T1187', name: 'Forced Authentication' },
      { id: 'T1003', name: 'OS Credential Dumping', subtechniques: ['T1003.001', 'T1003.002', 'T1003.003', 'T1003.004', 'T1003.005', 'T1003.006', 'T1003.007', 'T1003.008'] },
      { id: 'T1552', name: 'Unsecured Credentials', subtechniques: ['T1552.001', 'T1552.002', 'T1552.003', 'T1552.004', 'T1552.005'] }
    ],
    'TA0007': [
      { id: 'T1087', name: 'Account Discovery', subtechniques: ['T1087.001', 'T1087.002', 'T1087.003', 'T1087.004'] },
      { id: 'T1010', name: 'Application Window Discovery' },
      { id: 'T1217', name: 'Browser Information Discovery' },
      { id: 'T1580', name: 'Cloud Infrastructure Discovery' },
      { id: 'T1046', name: 'Network Service Discovery' },
      { id: 'T1135', name: 'Network Share Discovery' },
      { id: 'T1040', name: 'Network Sniffing' },
      { id: 'T1057', name: 'Process Discovery' },
      { id: 'T1018', name: 'Remote System Discovery' },
      { id: 'T1082', name: 'System Information Discovery' },
      { id: 'T1016', name: 'System Network Configuration Discovery', subtechniques: ['T1016.001'] },
      { id: 'T1049', name: 'System Network Connections Discovery' }
    ],
    'TA0008': [
      { id: 'T1210', name: 'Exploitation of Remote Services' },
      { id: 'T1534', name: 'Internal Spearphishing' },
      { id: 'T1570', name: 'Lateral Tool Transfer' },
      { id: 'T1021', name: 'Remote Services', subtechniques: ['T1021.001', 'T1021.002', 'T1021.003', 'T1021.004', 'T1021.005', 'T1021.006', 'T1021.007'] },
      { id: 'T1080', name: 'Taint Shared Content' },
      { id: 'T1550', name: 'Use Alternate Authentication Material', subtechniques: ['T1550.001', 'T1550.002', 'T1550.003', 'T1550.004'] }
    ],
    'TA0009': [
      { id: 'T1560', name: 'Archive Collected Data', subtechniques: ['T1560.001', 'T1560.002', 'T1560.003'] },
      { id: 'T1123', name: 'Audio Capture' },
      { id: 'T1119', name: 'Automated Collection' },
      { id: 'T1115', name: 'Clipboard Data' },
      { id: 'T1530', name: 'Data from Cloud Storage' },
      { id: 'T1005', name: 'Data from Local System' },
      { id: 'T1039', name: 'Data from Network Shared Drive' },
      { id: 'T1113', name: 'Screen Capture' },
      { id: 'T1125', name: 'Video Capture' }
    ],
    'TA0011': [
      { id: 'T1071', name: 'Application Layer Protocol', subtechniques: ['T1071.001', 'T1071.002', 'T1071.003', 'T1071.004'] },
      { id: 'T1132', name: 'Data Encoding', subtechniques: ['T1132.001', 'T1132.002'] },
      { id: 'T1001', name: 'Data Obfuscation', subtechniques: ['T1001.001', 'T1001.002', 'T1001.003'] },
      { id: 'T1573', name: 'Encrypted Channel', subtechniques: ['T1573.001', 'T1573.002'] },
      { id: 'T1008', name: 'Fallback Channels' },
      { id: 'T1105', name: 'Ingress Tool Transfer' },
      { id: 'T1095', name: 'Non-Application Layer Protocol' },
      { id: 'T1571', name: 'Non-Standard Port' },
      { id: 'T1572', name: 'Protocol Tunneling' },
      { id: 'T1090', name: 'Proxy', subtechniques: ['T1090.001', 'T1090.002', 'T1090.003', 'T1090.004'] },
      { id: 'T1219', name: 'Remote Access Software' }
    ],
    'TA0010': [
      { id: 'T1020', name: 'Automated Exfiltration', subtechniques: ['T1020.001'] },
      { id: 'T1030', name: 'Data Transfer Size Limits' },
      { id: 'T1048', name: 'Exfiltration Over Alternative Protocol', subtechniques: ['T1048.001', 'T1048.002', 'T1048.003'] },
      { id: 'T1041', name: 'Exfiltration Over C2 Channel' },
      { id: 'T1567', name: 'Exfiltration Over Web Service', subtechniques: ['T1567.001', 'T1567.002', 'T1567.003', 'T1567.004'] },
      { id: 'T1029', name: 'Scheduled Transfer' }
    ],
    'TA0040': [
      { id: 'T1531', name: 'Account Access Removal' },
      { id: 'T1485', name: 'Data Destruction' },
      { id: 'T1486', name: 'Data Encrypted for Impact' },
      { id: 'T1565', name: 'Data Manipulation', subtechniques: ['T1565.001', 'T1565.002', 'T1565.003'] },
      { id: 'T1491', name: 'Defacement', subtechniques: ['T1491.001', 'T1491.002'] },
      { id: 'T1561', name: 'Disk Wipe', subtechniques: ['T1561.001', 'T1561.002'] },
      { id: 'T1499', name: 'Endpoint Denial of Service', subtechniques: ['T1499.001', 'T1499.002', 'T1499.003', 'T1499.004'] },
      { id: 'T1495', name: 'Firmware Corruption' },
      { id: 'T1490', name: 'Inhibit System Recovery' },
      { id: 'T1489', name: 'Service Stop' },
      { id: 'T1529', name: 'System Shutdown/Reboot' }
    ]
  }
};

// ICS Assets (MITRE ATT\u202F&\u202FCK v18)
const ICS_ASSETS = [
  { id: 'A0017', name: 'DCS Controller', description: 'Distributed Control System Controller - microprocessor units managing industrial processes', icon: Cpu, sector: 'ICS' },
  { id: 'A0016', name: 'Firewall', description: 'Gateways enforcing network access policies in ICS environments', icon: Shield, sector: 'ICS' },
  { id: 'A0015', name: 'Switch', description: 'Network switches in industrial control systems', icon: Network, sector: 'ICS' },
  { id: 'A0009', name: 'Data Gateway', description: 'Devices that bridge communication between different protocols', icon: Server, sector: 'ICS' },
  { id: 'A0006', name: 'Data Historian', description: 'Databases storing historical process data', icon: Database, sector: 'ICS' },
  { id: 'A0001', name: 'Control Server', description: 'Servers hosting SCADA and control applications', icon: Server, sector: 'ICS' },
  { id: 'A0002', name: 'Engineering Workstation', description: 'Workstations for programming and configuring controllers', icon: Terminal, sector: 'ICS' },
  { id: 'A0003', name: 'HMI', description: 'Human-Machine Interface for operator interaction', icon: Settings, sector: 'ICS' },
  { id: 'A0004', name: 'PLC', description: 'Programmable Logic Controller for automation', icon: Cpu, sector: 'ICS' },
  { id: 'A0005', name: 'RTU', description: 'Remote Terminal Unit for remote monitoring', icon: Radio, sector: 'ICS' }
];

// Mapping vulns vers techniques
const VULN_TO_MITRE = {
  'nmap': ['T1046', 'T1018', 'T1082'],
  'network_discovery': ['T1046', 'T1018', 'T1595'],
  'port_scan': ['T1046', 'T1595.001'],
  'brute_force': ['T1110'],
  'credential_leak': ['T1552', 'T1589.001'],
  'password_spray': ['T1110.003'],
  'kerberoasting': ['T1558.003'],
  'pass_the_hash': ['T1550.002'],
  'mimikatz': ['T1003.001'],
  'lsass_dump': ['T1003.001'],
  'phishing': ['T1566'],
  'exploit_public': ['T1190'],
  'valid_accounts': ['T1078'],
  'smb_exploit': ['T1210', 'T1021.002'],
  'rdp_exploit': ['T1210', 'T1021.001'],
  'ssh_exploit': ['T1210', 'T1021.004'],
  'sql_injection': ['T1190', 'T1059'],
  'xss': ['T1189'],
  'rce': ['T1190', 'T1059'],
  'samba': ['T1210', 'T1021.002'],
  'smb': ['T1021.002', 'T1135'],
  'CVE-2017-7494': ['T1210', 'T1059'],
  'CVE-2017-0144': ['T1210', 'T1021.002'],
  'CVE-2020-1472': ['T1068', 'T1003'],
  'CVE-2021-44228': ['T1190', 'T1059'],
  'CVE-2019-0708': ['T1210', 'T1021.001'],
};

// AI Quick prompts for MITRE assistance
const AI_PROMPTS = [
  { icon: Target, label: 'Stratégie de détection', query: 'Quelles sont les stratégies de détection recommandées pour les techniques détectées?' },
  { icon: Shield, label: 'Contre-mesures', query: 'Quelles mitigations appliquer pour contrer les techniques identifiées?' },
  { icon: AlertTriangle, label: 'Analyse Kill Chain', query: 'Analyse la kill chain potentielle basée sur les techniques détectées' },
  { icon: Network, label: 'Mouvement latéral', query: 'Identifie les chemins de mouvement latéral possibles' },
  { icon: Lock, label: 'Priorités', query: 'Quelles techniques représentent le plus grand risque et doivent être traitées en priorité?' },
  { icon: FileText, label: 'Rapport MITRE', query: 'Génère un rapport de couverture MITRE ATT\u202F&\u202FCK' }
];

const MITRENavigatorPage = () => {
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detectedTechniques, setDetectedTechniques] = useState(new Set());
  const [hoveredTechnique, setHoveredTechnique] = useState(null);
  const [selectedTactic, setSelectedTactic] = useState(null);
  const [showOnlyDetected, setShowOnlyDetected] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState('matrix');
  const [searchTerm, setSearchTerm] = useState('');

  // AI Assistant state
  const [showAIPanel, setShowAIPanel] = useState(true);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);

  // Load data
  useEffect(() => {
    const fetchData = async () => {
      // Check if token is valid before making requests
      if (!auth.isTokenValidClientSide()) {
        setLoading(false);
        return;
      }

      try {
        const [scansRes, vulnsRes, sessionsRes] = await Promise.all([
          apiClient.get('/scan').catch(() => ({ data: { scans: [] } })),
          apiClient.get('/vulnerabilities').catch(() => ({ data: { vulnerabilities: [] } })),
          apiClient.get('/exploit/sessions').catch(() => ({ data: { sessions: [] } }))
        ]);

        setScans(scansRes.data?.scans || scansRes.data || []);
        setVulnerabilities(vulnsRes.data?.vulnerabilities || vulnsRes.data || []);
        setSessions(sessionsRes.data?.sessions || sessionsRes.data || []);
      } catch (error) {
        // Only log non-auth errors (auth errors are handled by apiClient interceptor)
        if (!error.isAuthRedirect) {
          console.error('Error fetching data:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Analyze and map techniques
  useEffect(() => {
    const detected = new Set();

    vulnerabilities.forEach(vuln => {
      const vulnText = `${vuln.title || ''} ${vuln.description || ''} ${vuln.cve_id || ''} ${vuln.discovered_by || ''}`.toLowerCase();
      Object.entries(VULN_TO_MITRE).forEach(([keyword, techniques]) => {
        if (vulnText.includes(keyword.toLowerCase())) {
          techniques.forEach(t => detected.add(t));
        }
      });
      if (vuln.cve_id && VULN_TO_MITRE[vuln.cve_id]) {
        VULN_TO_MITRE[vuln.cve_id].forEach(t => detected.add(t));
      }
    });

    scans.forEach(scan => {
      if (scan.scan_type === 'network_discovery') ['T1046', 'T1018', 'T1595'].forEach(t => detected.add(t));
      if (scan.scan_type === 'vulnerability_scan') ['T1595', 'T1046'].forEach(t => detected.add(t));
      if (scan.scan_type === 'full_autonomous') ['T1595', 'T1046', 'T1018', 'T1082', 'T1083'].forEach(t => detected.add(t));
    });

    sessions.forEach(session => {
      if (session.exploit_module) {
        const module = session.exploit_module.toLowerCase();
        if (module.includes('smb') || module.includes('samba')) ['T1210', 'T1021.002'].forEach(t => detected.add(t));
        if (module.includes('ssh')) ['T1021.004', 'T1110'].forEach(t => detected.add(t));
        if (module.includes('rdp')) ['T1021.001', 'T1210'].forEach(t => detected.add(t));
      }
    });

    if (detected.size === 0 && (vulnerabilities.length > 0 || scans.length > 0)) {
      ['T1595', 'T1046', 'T1018'].forEach(t => detected.add(t));
    }

    setDetectedTechniques(detected);
  }, [vulnerabilities, scans, sessions]);

  const getTechniqueColor = (techId, tacticColor) => {
    if (detectedTechniques.has(techId)) {
      return { bg: `${tacticColor}40`, border: tacticColor, text: '#fff' };
    }
    return { bg: '#1a1a1a', border: '#333', text: '#666' };
  };

  // AI Assistant
  const askAI = async (query) => {
    setAiLoading(true);
    try {
      const response = await apiClient.post('/ai/analyze', {
        query,
        context: {
          detected_techniques: Array.from(detectedTechniques),
          vulnerabilities: vulnerabilities.slice(0, 20),
          scans: scans.slice(0, 10),
          sessions: sessions.slice(0, 10),
          type: 'mitre_analysis'
        }
      });

      const data = response.data;
      const result = {
        query,
        response: data.response || data.analysis,
        suggestions: data.suggestions || [],
        timestamp: new Date().toISOString()
      };
      setAiResponse(result);
      setAiHistory(prev => [result, ...prev.slice(0, 9)]);
    } catch (err) {
      // Fall back to local analysis if API fails (including auth errors)
      if (!err.isAuthRedirect) {
        setAiResponse(generateLocalMITREAnalysis(query));
      }
    } finally {
      setAiLoading(false);
      setAiQuery('');
    }
  };

  const generateLocalMITREAnalysis = (query) => {
    const lowerQuery = query.toLowerCase();
    let response = '';
    let suggestions = [];
    const techArray = Array.from(detectedTechniques);

    if (lowerQuery.includes('detection') || lowerQuery.includes('strateg')) {
      response = `Stratégies de Détection MITRE ATT\u202F&\u202FCK v18:\n\n`;
      response += `Techniques détectées: ${techArray.length}\n\n`;
      response += `Recommandations de détection:\n\n`;
      if (techArray.includes('T1046')) response += `- T1046 Network Service Discovery:\n  * Monitorer les scans de ports anormaux\n  * Analyser les logs firewall pour patterns de scan\n  * Déployer IDS/IPS avec signatures de scan\n\n`;
      if (techArray.includes('T1110')) response += `- T1110 Brute Force:\n  * Configurer alertes sur échecs d'auth multiples\n  * Implémenter rate limiting\n  * Surveiller les tentatives de connexion\n\n`;
      if (techArray.includes('T1021.002')) response += `- T1021.002 SMB/Windows Admin Shares:\n  * Monitorer Event ID 5140, 5145\n  * Analyser les connexions latérales\n  * Vérifier accès aux partages admin\n\n`;
      response += `Data Components v18 recommandés:\n- Network Traffic Flow\n- Process Creation\n- Authentication Logs\n- Command Execution`;
      suggestions = ['Voir Analytics', 'Générer règles SIEM', 'Exporter IOCs'];
    } else if (lowerQuery.includes('mitigation') || lowerQuery.includes('contrer')) {
      response = `Mitigations MITRE ATT\u202F&\u202FCK:\n\n`;
      response += `Pour les ${techArray.length} techniques détectées:\n\n`;
      response += `M1030 - Network Segmentation:\n- Isoler les systèmes critiques\n- Implémenter VLAN et micro-segmentation\n\n`;
      response += `M1026 - Privileged Account Management:\n- Appliquer le principe du moindre privilège\n- Utiliser PAM (Privileged Access Management)\n\n`;
      response += `M1018 - User Account Management:\n- Auditer régulièrement les comptes\n- Désactiver comptes inutilisés\n\n`;
      response += `M1031 - Network Intrusion Prevention:\n- Déployer IPS/IDS\n- Mettre à jour les signatures`;
      suggestions = ['Plan de remédiation', 'Priorités', 'Timeline'];
    } else if (lowerQuery.includes('kill chain') || lowerQuery.includes('chain')) {
      response = `Analyse Kill Chain - Techniques Détectées:\n\n`;
      response += `Phase 1 - Reconnaissance:\n`;
      const recon = techArray.filter(t => t.startsWith('T159') || t === 'T1595');
      response += recon.length > 0 ? `  Détecté: ${recon.join(', ')}\n` : '  Non détecté\n';
      response += `\nPhase 2 - Initial Access:\n`;
      const initial = techArray.filter(t => ['T1190', 'T1566', 'T1078', 'T1133'].includes(t));
      response += initial.length > 0 ? `  Détecté: ${initial.join(', ')}\n` : '  Non détecté\n';
      response += `\nPhase 3 - Execution:\n`;
      const exec = techArray.filter(t => t.startsWith('T1059') || t === 'T1047');
      response += exec.length > 0 ? `  Détecté: ${exec.join(', ')}\n` : '  Non détecté\n';
      response += `\nPhase 4 - Lateral Movement:\n`;
      const lateral = techArray.filter(t => t.startsWith('T1021') || t === 'T1210');
      response += lateral.length > 0 ? `  Détecté: ${lateral.join(', ')}\n` : '  Non détecté\n';
      response += `\nCouverture Kill Chain: ${Math.round((recon.length + initial.length + exec.length + lateral.length) / 4 * 25)} %`;
      suggestions = ['Gaps analysis', 'Threat scenarios', 'Red team paths'];
    } else if (lowerQuery.includes('lateral') || lowerQuery.includes('movement')) {
      response = `Analyse Mouvement Latéral:\n\n`;
      response += `Techniques de mouvement latéral détectées:\n\n`;
      const lateralTechs = ['T1021.001', 'T1021.002', 'T1021.003', 'T1021.004', 'T1210', 'T1570', 'T1550'];
      const detected = lateralTechs.filter(t => techArray.some(d => d.startsWith(t.split('.')[0])));
      detected.forEach(t => {
        if (t === 'T1021.001') response += `- T1021.001 RDP: Accès distant Windows\n`;
        if (t === 'T1021.002') response += `- T1021.002 SMB: Partages Windows/Samba\n`;
        if (t === 'T1021.004') response += `- T1021.004 SSH: Accès Unix/Linux\n`;
        if (t === 'T1210') response += `- T1210: Exploitation de services distants\n`;
      });
      response += `\nChemins potentiels identifiés: ${detected.length}\n`;
      response += `\nRecommandations:\n- Activer MFA sur tous les accès distants\n- Monitorer les connexions latérales\n- Limiter les privilèges administratifs`;
      suggestions = ['Network map', 'Critical paths', 'Segmentation'];
    } else if (lowerQuery.includes('priorit') || lowerQuery.includes('risque')) {
      response = `Priorisation des Risques:\n\n`;
      const criticalTechs = techArray.filter(t => ['T1003', 'T1059', 'T1190', 'T1210', 'T1486'].some(c => t.startsWith(c)));
      const highTechs = techArray.filter(t => ['T1021', 'T1110', 'T1078', 'T1566'].some(c => t.startsWith(c)));
      response += `CRITIQUES (${criticalTechs.length}):\n`;
      criticalTechs.forEach(t => response += `  - ${t}\n`);
      response += `\nÉLEVÉS (${highTechs.length}):\n`;
      highTechs.forEach(t => response += `  - ${t}\n`);
      response += `\nACTIONS IMMÉDIATES:\n`;
      response += `1. Patcher les vulnérabilités exploitables\n`;
      response += `2. Renforcer les authentifications\n`;
      response += `3. Segmenter le réseau\n`;
      response += `4. Déployer détection avancée`;
      suggestions = ['Plan action', 'Timeline remédiation', 'Quick wins'];
    } else if (lowerQuery.includes('rapport') || lowerQuery.includes('report')) {
      response = `Rapport Couverture MITRE ATT\u202F&\u202FCK:\n\n`;
      response += `Date: ${new Date().toLocaleDateString('fr-FR')}\n`;
      response += `Version: ATT&CK v18\n\n`;
      response += `RÉSUMÉ EXÉCUTIF:\n`;
      response += `- Techniques détectées: ${techArray.length}\n`;
      response += `- Tactiques couvertes: ${new Set(techArray.map(t => {
        for (const [tactic, techs] of Object.entries(MITRE_MATRIX.techniques)) {
          if (techs.some(tech => tech.id === t || t.startsWith(tech.id))) return tactic;
        }
        return null;
      }).filter(Boolean)).size}/14\n`;
      response += `- Vulnérabilités mappées: ${vulnerabilities.length}\n`;
      response += `- Sessions exploitation: ${sessions.length}\n\n`;
      response += `TECHNIQUES PAR TACTIQUE:\n`;
      MITRE_MATRIX.tactics.forEach(tactic => {
        const count = techArray.filter(t =>
          MITRE_MATRIX.techniques[tactic.id]?.some(tech => tech.id === t || t.startsWith(tech.id))
        ).length;
        if (count > 0) response += `- ${tactic.shortName}: ${count}\n`;
      });
      suggestions = ['Export PDF', 'Export Navigator JSON', 'Share'];
    } else {
      response = `Analyse MITRE ATT\u202F&\u202FCK v18:\n\n`;
      response += `Statistiques actuelles:\n`;
      response += `- Techniques détectées: ${techArray.length}\n`;
      response += `- Vulnérabilités: ${vulnerabilities.length}\n`;
      response += `- Scans effectués: ${scans.length}\n`;
      response += `- Sessions actives: ${sessions.length}\n\n`;
      response += `Nouveautés ATT&CK v18:\n`;
      response += `- Stratégies de détection et analyses\n`;
      response += `- Actifs ICS étendus (DCS, Pare-feu, Commutateur)\n`;
      response += `- Composants de données améliorés\n`;
      response += `- Mises à jour Mobile et Cloud\n\n`;
      response += `Posez une question spécifique pour une analyse détaillée.`;
      suggestions = ['Stratégies de détection', 'Mitigations', 'Kill chain', 'Priorités'];
    }

    const result = { query, response, suggestions, timestamp: new Date().toISOString(), isLocal: true };
    setAiHistory(prev => [result, ...prev.slice(0, 9)]);
    return result;
  };

  const exportToNavigator = () => {
    const layer = {
      name: `SYLink Pentest - ${new Date().toLocaleDateString('fr-FR')}`,
      versions: { attack: "18", navigator: "4.9.1", layer: "4.5" },
      domain: "enterprise-attack",
      description: "Techniques détectées par SYLink Pentest",
      techniques: Array.from(detectedTechniques).map(techId => ({
        techniqueID: techId,
        color: "#ff6666",
        comment: "Détecté par scan",
        enabled: true,
        score: 100
      })),
      gradient: { colors: ["#ffffff", "#ff6666"], minValue: 0, maxValue: 100 }
    };
    const blob = new Blob([JSON.stringify(layer, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mitre_navigator_layer_${Date.now()}.json`;
    a.click();
  };

  const exportToExcel = async () => {
    let XLSX;
    try {
      XLSX = await import('xlsx');
    } catch {
      toast.error('Bibliothèque xlsx non disponible — export JSON effectué');
      exportToNavigator();
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    const wb = XLSX.utils.book_new();

    // Helper: technique or any of its subtechniques detected
    const isTechDetected = (id) =>
      detectedTechniques.has(id) ||
      Array.from(detectedTechniques).some(d => d.startsWith(id + '.'));

    // ---- Sheet 1: Full Matrix ----
    const matrixHeader = [
      'Tactic ID', 'Tactic Name', 'Technique ID', 'Technique Name',
      'Sub-Technique ID', 'Type', 'Détecté (Workspace)', 'URL MITRE'
    ];
    const matrixRows = [matrixHeader];

    MITRE_MATRIX.tactics.forEach(tactic => {
      const techs = MITRE_MATRIX.techniques[tactic.id] || [];
      techs.forEach(tech => {
        const techDetected = isTechDetected(tech.id) ? 'OUI' : 'NON';
        const techUrl = `https://attack.mitre.org/techniques/${tech.id}/`;
        matrixRows.push([
          tactic.id, tactic.name, tech.id, tech.name, '', 'Technique', techDetected, techUrl
        ]);
        if (tech.subtechniques) {
          tech.subtechniques.forEach(subId => {
            const subDetected = detectedTechniques.has(subId) ? 'OUI' : 'NON';
            const subNum = subId.split('.')[1];
            const subUrl = `https://attack.mitre.org/techniques/${tech.id}/${subNum}/`;
            matrixRows.push([
              tactic.id, tactic.name, tech.id, tech.name, subId, 'Sub-Technique', subDetected, subUrl
            ]);
          });
        }
      });
    });

    const ws1 = XLSX.utils.aoa_to_sheet(matrixRows);
    ws1['!cols'] = [
      { wch: 10 }, { wch: 25 }, { wch: 13 }, { wch: 46 },
      { wch: 16 }, { wch: 14 }, { wch: 22 }, { wch: 56 }
    ];
    XLSX.utils.book_append_sheet(wb, ws1, 'Matrice ATT&CK v18');

    // ---- Sheet 2: Detected Only ----
    const detectedRows = [matrixHeader, ...matrixRows.slice(1).filter(r => r[6] === 'OUI')];
    const ws2 = XLSX.utils.aoa_to_sheet(detectedRows);
    ws2['!cols'] = ws1['!cols'];
    XLSX.utils.book_append_sheet(wb, ws2, 'Techniques Détectées');

    // ---- Sheet 3: Stats by Tactic ----
    const statsHeader = [
      'Tactic ID', 'Tactic Name', 'Techniques Parentes', 'Total (avec Sous-Tech)',
      'Détectées', '% Couverture'
    ];
    const statsRows = [statsHeader];
    let grandTotal = 0, grandDetected = 0;

    MITRE_MATRIX.tactics.forEach(tactic => {
      const techs = MITRE_MATRIX.techniques[tactic.id] || [];
      let total = 0, detected = 0;
      techs.forEach(tech => {
        total++;
        if (isTechDetected(tech.id)) detected++;
        (tech.subtechniques || []).forEach(subId => {
          total++;
          if (detectedTechniques.has(subId)) detected++;
        });
      });
      grandTotal += total;
      grandDetected += detected;
      const pct = total > 0 ? ((detected / total) * 100).toFixed(1) : '0.0';
      statsRows.push([tactic.id, tactic.name, techs.length, total, detected, `${pct}%`]);
    });
    // Grand total row
    const grandPct = grandTotal > 0 ? ((grandDetected / grandTotal) * 100).toFixed(1) : '0.0';
    statsRows.push(['TOTAL', 'Tous les tactiques', '', grandTotal, grandDetected, `${grandPct}%`]);

    const ws3 = XLSX.utils.aoa_to_sheet(statsRows);
    ws3['!cols'] = [
      { wch: 10 }, { wch: 25 }, { wch: 20 }, { wch: 24 }, { wch: 12 }, { wch: 14 }
    ];
    XLSX.utils.book_append_sheet(wb, ws3, 'Statistiques');

    XLSX.writeFile(wb, `MITRE_ATT_CK_Enterprise_v18_${today}.xlsx`);
    toast.success(`Export Excel généré — ${detectedRows.length - 1} techniques détectées`);
  };

  const totalTechniques = Object.values(MITRE_MATRIX.techniques).flat().length;
  const coveragePercent = Math.round((detectedTechniques.size / totalTechniques) * 100);

  return (
    <div className="min-h-screen bg-brand-black p-6">
      <div className="w-[80%] mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/reports')}
              className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-4xl font-bold text-gradient-green mb-2 flex items-center gap-3">
                <Shield className="w-10 h-10 text-red-500" />
                {'MITRE ATT\u202F&\u202FCK Navigator'}
                <span className="text-sm bg-red-500/20 text-red-400 px-3 py-1 rounded-full">v18</span>
              </h1>
              <p className="text-gray-400">Framework de référence pour la détection et remédiation des menaces</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAIPanel(!showAIPanel)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showAIPanel ? 'bg-brand-green text-black' : 'bg-brand-green/20 text-brand-green hover:bg-brand-green/30'
              }`}
            >
              <Brain className="w-4 h-4" />
              Assistant IA
            </button>
            <button
              onClick={exportToExcel}
              className="px-4 py-2 bg-green-500/20 text-green-400 border border-green-500/50 rounded-lg hover:bg-green-500/30 transition-colors flex items-center gap-2"
            >
              <Download size={16} />
              Exporter Excel
            </button>
            <a
              href="https://attack.mitre.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-gray-800 text-gray-300 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <ExternalLink size={16} />
              Site officiel
            </a>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-brand-black-light rounded-xl p-4 border border-red-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Zap className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-red-400">{detectedTechniques.size}</div>
                <div className="text-sm text-gray-400">Détectées</div>
              </div>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-gray-600/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-600/20 flex items-center justify-center">
                <Layers className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{totalTechniques}</div>
                <div className="text-sm text-gray-400">Total techniques</div>
              </div>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center">
                <Activity className="w-5 h-5 text-brand-green" />
              </div>
              <div>
                <div className="text-2xl font-bold text-brand-green">{coveragePercent} %</div>
                <div className="text-sm text-gray-400">Couverture</div>
              </div>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-purple-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-400">{vulnerabilities.length}</div>
                <div className="text-sm text-gray-400">Vulnérabilités</div>
              </div>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-orange-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-400">{scans.length}</div>
                <div className="text-xs text-gray-500">Scans</div>
              </div>
            </div>
          </div>
          <div className="bg-brand-black-light rounded-xl p-4 border border-cyan-500/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <Terminal className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-cyan-400">{sessions.length}</div>
                <div className="text-xs text-gray-500">Sessions</div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Assistant Panel */}
        {showAIPanel && (
          <div className="bg-brand-black-light rounded-xl border border-blue-500/30 overflow-hidden mb-6">
            <div className="p-4 border-b border-blue-500/20 bg-blue-500/10">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-blue-400" />
                {'Assistant IA - Analyse MITRE ATT\u202F&\u202FCK'}
                <span className="ml-2 text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">LLM</span>
                <span className="ml-1 text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded">v18</span>
              </h3>
              <p className="text-sm text-gray-400 mt-1">Stratégies de détection, analyses, mitigations et recommandations basées sur ATT&CK v18</p>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex flex-wrap gap-2">
                {AI_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => askAI(prompt.query)}
                    disabled={aiLoading}
                    className="px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors flex items-center gap-2 text-sm"
                  >
                    <prompt.icon className="w-4 h-4" />
                    {prompt.label}
                  </button>
                ))}
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && aiQuery && askAI(aiQuery)}
                  placeholder="Question MITRE (techniques, détection, mitigations...)"
                  className="flex-1 px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500"
                />
                <button
                  onClick={() => aiQuery && askAI(aiQuery)}
                  disabled={aiLoading || !aiQuery}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {aiLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Analyser
                </button>
              </div>

              {aiResponse && (
                <div className="bg-brand-black rounded-lg p-4 border border-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-gray-400">Analyse MITRE</span>
                        {aiResponse.isLocal && <span className="text-xs px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">Local</span>}
                      </div>
                      <MarkdownRenderer content={aiResponse.response} variant="compact" />
                      {aiResponse.suggestions && aiResponse.suggestions.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="text-xs text-gray-500 mb-2">Suggestions:</div>
                          <div className="flex flex-wrap gap-2">
                            {aiResponse.suggestions.map((s, i) => (
                              <button key={i} onClick={() => askAI(s)} className="px-2 py-1 bg-blue-500/10 text-blue-400 rounded text-xs hover:bg-blue-500/20 flex items-center gap-1">
                                <Lightbulb className="w-3 h-3" />{s}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('matrix')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === 'matrix' ? 'bg-brand-green text-brand-black font-bold' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Layers size={16} />
            Matrice Entreprise
          </button>
          <button
            onClick={() => setActiveTab('ics')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === 'ics' ? 'bg-brand-green text-brand-black font-bold' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Factory size={16} />
            Actifs ICS v18
          </button>
          <button
            onClick={() => setActiveTab('detection')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === 'detection' ? 'bg-brand-green text-brand-black font-bold' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Eye size={16} />
            Stratégies de détection
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === 'history' ? 'bg-brand-green text-brand-black font-bold' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <BookOpen size={16} />
            Historique IA
          </button>
          <button
            onClick={() => setActiveTab('reference')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === 'reference' ? 'bg-brand-green text-brand-black font-bold' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <FileText size={16} />
            Référence
          </button>
        </div>

        {/* Matrix Tab */}
        {activeTab === 'matrix' && (
          <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
            <div className="p-4 border-b border-brand-green/20 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-bold text-white">Matrice Entreprise ATT&CK v18</h2>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500/40 border-2 border-red-500 rounded"></div>
                  <span className="text-xs text-gray-400">Détectée</span>
                  <div className="w-4 h-4 bg-gray-800 border border-gray-600 rounded ml-2"></div>
                  <span className="text-xs text-gray-400">Non détectée</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowOnlyDetected(!showOnlyDetected)}
                  className={`p-2 rounded-lg transition-colors ${
                    showOnlyDetected ? 'bg-red-500/20 text-red-400 border border-red-500/50' : 'bg-gray-800 text-gray-400 border border-gray-700'
                  }`}
                >
                  {showOnlyDetected ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-lg bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700"
                >
                  <Maximize2 size={18} />
                </button>
              </div>
            </div>

            <div className="p-4 overflow-x-auto">
              <div className="inline-flex gap-1 min-w-max">
                {MITRE_MATRIX.tactics.map((tactic) => {
                  const tactics_techniques = MITRE_MATRIX.techniques[tactic.id] || [];
                  const filteredTechniques = showOnlyDetected
                    ? tactics_techniques.filter(t => detectedTechniques.has(t.id))
                    : tactics_techniques;

                  if (showOnlyDetected && filteredTechniques.length === 0) return null;

                  const detectedInTactic = tactics_techniques.filter(t => detectedTechniques.has(t.id)).length;

                  return (
                    <div key={tactic.id} className="flex-shrink-0" style={{ width: '130px' }}>
                      <div
                        className="p-2 text-center font-bold text-white text-xs rounded-t-lg cursor-pointer"
                        style={{ backgroundColor: tactic.color }}
                        title={tactic.description}
                      >
                        <div className="truncate">{tactic.shortName}</div>
                        {detectedInTactic > 0 && (
                          <div className="text-[10px] opacity-80 mt-0.5">
                            <span className="bg-white/20 px-1.5 py-0.5 rounded">{detectedInTactic}</span>
                          </div>
                        )}
                      </div>

                      <div className="space-y-0.5 p-1 bg-brand-black rounded-b-lg border border-gray-800" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {filteredTechniques.map((tech) => {
                          const colors = getTechniqueColor(tech.id, tactic.color);
                          const isDetected = detectedTechniques.has(tech.id);

                          return (
                            <div
                              key={tech.id}
                              onMouseEnter={() => setHoveredTechnique({ ...tech, tactic })}
                              onMouseLeave={() => setHoveredTechnique(null)}
                              className={`p-1.5 rounded text-[10px] cursor-pointer transition-all ${isDetected ? 'hover:scale-105' : 'hover:bg-gray-800'}`}
                              style={{ backgroundColor: colors.bg, borderLeft: `3px solid ${colors.border}`, color: colors.text }}
                            >
                              <div className="font-mono font-bold">{tech.id}</div>
                              <div className="leading-tight truncate" title={tech.name}>{tech.name}</div>
                              {tech.subtechniques && <div className="text-[8px] opacity-60 mt-0.5">+{tech.subtechniques.length} sub</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {hoveredTechnique && (
              <div className="mx-4 mb-4 p-4 bg-gradient-to-r from-brand-black to-brand-black-light rounded-lg border border-brand-green/30">
                <div className="flex items-start gap-4">
                  <div className="px-3 py-2 rounded font-mono text-sm font-bold" style={{ backgroundColor: `${hoveredTechnique.tactic.color}20`, color: hoveredTechnique.tactic.color, border: `1px solid ${hoveredTechnique.tactic.color}50` }}>
                    {hoveredTechnique.id}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-white text-lg">{hoveredTechnique.name}</h3>
                      {detectedTechniques.has(hoveredTechnique.id) && (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full border border-red-500/50 flex items-center gap-1">
                          <Zap size={12} />DÉTECTÉE
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-400 mb-2">
                      Tactique: <span style={{ color: hoveredTechnique.tactic.color }}>{hoveredTechnique.tactic.name}</span>
                    </div>
                    {hoveredTechnique.subtechniques && (
                      <div className="text-xs text-gray-500">Sub-techniques: {hoveredTechnique.subtechniques.join(', ')}</div>
                    )}
                    <a href={`https://attack.mitre.org/techniques/${hoveredTechnique.id}/`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-400 hover:underline mt-2">
                      {'Voir sur MITRE ATT\u202F&\u202FCK '}<ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ICS Assets Tab */}
        {activeTab === 'ics' && (
          <div className="bg-brand-black-light rounded-xl border border-orange-500/30 overflow-hidden">
            <div className="p-4 border-b border-orange-500/20 bg-orange-500/10">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Factory className="w-5 h-5 text-orange-400" />
                {'Actifs ICS - MITRE ATT\u202F&\u202FCK v18'}
                <span className="ml-2 text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded">NOUVEAU</span>
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                Nouveaux objets incluant contrôleur DCS, pare-feu, commutateur avec actifs associés
              </p>
            </div>

            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ICS_ASSETS.map((asset) => {
                const Icon = asset.icon;
                return (
                  <div key={asset.id} className="p-4 bg-brand-black rounded-lg border border-orange-500/20 hover:border-orange-500/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-orange-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded">{asset.id}</span>
                          <span className="text-xs text-gray-500">{asset.sector}</span>
                        </div>
                        <h3 className="font-semibold text-white mt-1">{asset.name}</h3>
                        <p className="text-xs text-gray-400 mt-1">{asset.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t border-orange-500/20">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-400">
                  <p className="font-medium text-orange-400 mb-1">Nouveautés ATT&CK v18 pour ICS :</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>3 nouveaux actifs : Contrôleur DCS (A0017), Pare-feu (A0016), Commutateur (A0015)</li>
                    <li>Champ actifs associés pour référencer les terminologies sectorielles</li>
                    <li>Descriptions clarifiées pour les passerelles de données et historiens</li>
                    <li>18 actifs, 83 techniques, 52 mesures d'atténuation, 83 stratégies de détection</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detection Strategies Tab */}
        {activeTab === 'detection' && (
          <div className="bg-brand-black-light rounded-xl border border-cyan-500/30 overflow-hidden">
            <div className="p-4 border-b border-cyan-500/20 bg-cyan-500/10">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-cyan-400" />
                Stratégies de détection et analytiques - v18
                <span className="ml-2 text-xs px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">NOUVEAU</span>
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                Nouvelles stratégies de détection et analytiques remplaçant les anciennes détections
              </p>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-brand-black rounded-lg border border-cyan-500/20">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-cyan-400" />
                    Stratégies de détection
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-400">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <span>Modèles de comportement spécifiques</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <span>Sources de télémétrie recommandées</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <span>Paramètres et seuils de détection</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <span>Exemples spécifiques par plateforme</span>
                    </li>
                  </ul>
                </div>

                <div className="p-4 bg-brand-black rounded-lg border border-cyan-500/20">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-cyan-400" />
                    Analytiques
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-400">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <span>Identifiants d'événements spécifiques Windows/Linux</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <span>Séquences de comportement à surveiller</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <span>Variations par système d'exploitation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <span>Règles SIEM pré-formatées</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="p-4 bg-brand-black rounded-lg border border-gray-700">
                <h3 className="font-semibold text-white mb-3">Composants de données recommandés</h3>
                <div className="flex flex-wrap gap-2">
                  {['Flux de trafic réseau', 'Création de processus', 'Exécution de commandes', 'Modification de fichiers', 'Authentification utilisateur', 'Clés de registre Windows'].map((dc, i) => (
                    <span key={i} className="px-3 py-1 bg-cyan-500/10 text-cyan-400 rounded-full text-xs border border-cyan-500/30">
                      {dc}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="bg-brand-black-light rounded-xl border border-purple-500/30 overflow-hidden">
            <div className="p-4 border-b border-purple-500/20 bg-purple-500/10">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-purple-400" />
                Historique des Analyses IA
              </h2>
            </div>

            <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
              {aiHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Brain className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune analyse effectuée</p>
                  <p className="text-xs mt-1">Utilisez l'assistant IA pour commencer</p>
                </div>
              ) : (
                aiHistory.map((item, i) => (
                  <div key={i} className="p-4 bg-brand-black rounded-lg border border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-purple-400 font-medium">{item.query}</span>
                      <span className="text-xs text-gray-500">{new Date(item.timestamp).toLocaleString('fr-FR')}</span>
                    </div>
                    <div className="max-h-[200px] overflow-y-auto">
                      <MarkdownRenderer content={item.response} variant="compact" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Reference Tab */}
        {activeTab === 'reference' && (
          <div className="bg-brand-black-light rounded-xl border border-brand-green/20 overflow-hidden">
            <div className="p-4 border-b border-brand-green/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield size={24} className="text-brand-green" />
                  <div>
                    <h2 className="text-xl font-bold text-white">{'Référence MITRE ATT\u202F&\u202FCK'}</h2>
                    <p className="text-sm text-gray-400">Tactiques d'attaque</p>
                  </div>
                </div>
                <a href="https://attack.mitre.org/" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-blue-400 hover:underline">
                  <ExternalLink size={16} />attack.mitre.org
                </a>
              </div>
            </div>

            <div className="p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">ID</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Tactique</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Description</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Exemples</th>
                  </tr>
                </thead>
                <tbody>
                  {MITRE_TACTICS_REF.map((tactic, i) => (
                    <tr key={tactic.id} className={`border-b border-gray-800 hover:bg-brand-black transition-colors ${i % 2 === 0 ? 'bg-brand-black/30' : ''}`}>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 rounded text-xs font-mono font-bold" style={{ backgroundColor: `${tactic.color}20`, color: tactic.color }}>
                          {tactic.id}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tactic.color }} />
                          <span className="font-semibold text-white">{tactic.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-400">{tactic.description}</td>
                      <td className="py-3 px-4 text-gray-500 text-xs">{tactic.examples}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-brand-green/20 bg-brand-black/50">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-400">{'Exporter le tableau MITRE ATT\u202F&\u202FCK'}</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      let csv = 'ID,Tactique,Description,Exemples\n';
                      MITRE_TACTICS_REF.forEach(t => csv += `"${t.id}","${t.name}","${t.description}","${t.examples}"\n`);
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `MITRE_ATT&CK_${new Date().toISOString().split('T')[0]}.csv`;
                      a.click();
                    }}
                    className="px-4 py-2 bg-green-500/20 text-green-400 border border-green-500/50 rounded-lg hover:bg-green-500/30 flex items-center gap-2 text-sm"
                  >
                    <Download size={16} />CSV
                  </button>
                  <button
                    onClick={() => {
                      const printContent = `<html><head><title>MITRE ATT\u202F&\u202FCK</title><style>body{font-family:Arial;padding:20px}h1{color:#333;border-bottom:2px solid #99ce16}table{width:100%;border-collapse:collapse}th{background:#1a1a1a;color:#99ce16;padding:12px;text-align:left}td{padding:10px;border-bottom:1px solid #ddd}tr:nth-child(even){background:#f9f9f9}</style></head><body><h1>MITRE ATT\u202F&\u202FCK - Tactiques</h1><table><thead><tr><th>ID</th><th>Tactique</th><th>Description</th><th>Exemples</th></tr></thead><tbody>${MITRE_TACTICS_REF.map(t => `<tr><td>${t.id}</td><td><strong>${t.name}</strong></td><td>${t.description}</td><td>${t.examples}</td></tr>`).join('')}</tbody></table><p style="margin-top:30px;font-size:12px;color:#666">Généré par SYLink Pentest - ${new Date().toLocaleDateString('fr-FR')}</p></body></html>`;
                      const w = window.open('', '_blank');
                      w.document.write(printContent);
                      w.document.close();
                      w.print();
                    }}
                    className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded-lg hover:bg-red-500/30 flex items-center gap-2 text-sm"
                  >
                    <Download size={16} />PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MITRENavigatorPage;
